import { ReactNode, type KeyboardEvent, type MouseEvent, useCallback, useState } from 'react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { GroupParticipant, getGroupParticipantDisplayName } from '@frontend/thread-header-context';
import { ListRow, NakedButton, styles, useTooltip } from '@frontend/design-system';
import {
  compactModeStyles,
  contactsPaneButtonStyles,
  containerStyles,
  editButtonStyles,
  editInputBoldStyles,
  editInputStyles,
  hiddenNamesTitleStyles,
  hiddenNamesTriggerStyles,
  namingWrapperStyles,
  tooltipContentStyles,
  visibleNamesTitleStyles,
} from './group-thread-title.styles';

const OVERFLOW_THRESHOLD = 4;
const MAX_VISIBLE_NAMES = 2;

type GroupThreadTitleProps = {
  participants: GroupParticipant[];
  isBold?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  isCompactMode?: boolean;
  showContactsPaneButton?: boolean;
  onGroupIconClick?: () => void;
  isEditable?: boolean;
  groupName?: string | null;
  onGroupNameSave?: (name: string | null) => void;
};

type HiddenNamesOverflowProps = {
  hiddenNames: string[];
  hiddenNamesTriggerProps: ReturnType<typeof useTooltip>['triggerProps'];
  isBold?: boolean;
  isCompactMode?: boolean;
};

const HiddenNamesOverflow = ({
  hiddenNames,
  hiddenNamesTriggerProps,
  isBold,
  isCompactMode,
}: HiddenNamesOverflowProps) => {
  const { t } = useTranslation('thread-header');

  return (
    <ListRow.Content.Title
      css={[hiddenNamesTitleStyles, isCompactMode && compactModeStyles]}
      isBold={isBold}
      isCompactMode={isCompactMode}
    >
      {', '}
      <span
        tabIndex={0}
        aria-label={t('Show hidden participant names')}
        title={hiddenNames.join(', ')}
        css={hiddenNamesTriggerStyles}
        {...hiddenNamesTriggerProps}
      >
        {t('{{count}} Others', { count: hiddenNames.length })}
      </span>
    </ListRow.Content.Title>
  );
};

export const GroupThreadTitle = ({
  participants,
  isBold,
  leadingIcon,
  trailingIcon,
  isCompactMode,
  showContactsPaneButton = false,
  onGroupIconClick,
  isEditable = false,
  groupName,
  onGroupNameSave,
}: GroupThreadTitleProps) => {
  const { t } = useTranslation('thread-header');
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');

  const displayNames = participants.map((participant) => getGroupParticipantDisplayName(participant)).filter(Boolean);
  const shouldTruncate = displayNames.length >= OVERFLOW_THRESHOLD;
  const visibleNames = shouldTruncate ? displayNames.slice(0, MAX_VISIBLE_NAMES) : displayNames;
  const hiddenNames = shouldTruncate ? displayNames.slice(MAX_VISIBLE_NAMES) : [];

  const {
    Tooltip,
    triggerProps: hiddenNamesTriggerProps,
    tooltipProps: hiddenNamesTooltipProps,
  } = useTooltip({
    trigger: 'hover',
    placement: 'top-start',
    offset: 8,
  });

  const {
    Tooltip: EditTooltip,
    triggerProps: editTriggerProps,
    tooltipProps: editTooltipProps,
  } = useTooltip({
    trigger: 'hover',
    placement: 'top',
    offset: 8,
  });

  const trimmedGroupName = groupName?.trim();
  const hasGroupName = !!trimmedGroupName;
  const displayTitle = hasGroupName ? trimmedGroupName : visibleNames.join(', ');
  const shouldShowHiddenNames = !hasGroupName && !!hiddenNames.length;

  const handleInputRef = useCallback((element: HTMLInputElement | null) => {
    if (!element) return;

    const length = element.value.length;
    element.focus();
    element.setSelectionRange(0, length, 'forward');
  }, []);

  const handleBeginEdit = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setEditValue(trimmedGroupName ?? displayNames.join(', '));
    setIsEditing(true);
  };

  const handleCommitSave = () => {
    const nextName = editValue.trim();
    onGroupNameSave?.(nextName || null);
    setIsEditing(false);
  };

  const handleEditKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.currentTarget.blur();
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      setIsEditing(false);
    }
  };

  const handleCountClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onGroupIconClick?.();
  };

  return (
    <>
      <div css={containerStyles}>
        {leadingIcon}
        <div css={namingWrapperStyles}>
          {isEditing && (
            <input
              ref={handleInputRef}
              value={editValue}
              onChange={(event) => setEditValue(event.target.value)}
              onKeyDown={handleEditKeyDown}
              onBlur={handleCommitSave}
              aria-label={t('Edit Group Chat Name')}
              data-trackingid='group-thread-title-edit-input'
              css={[editInputStyles, isCompactMode && compactModeStyles, isBold && editInputBoldStyles]}
            />
          )}

          {!isEditing && isEditable && (
            <>
              <NakedButton
                onClick={handleBeginEdit}
                aria-label={t('Edit Group Chat Name')}
                data-trackingid='group-thread-title-edit-button'
                css={editButtonStyles}
              >
                <span {...editTriggerProps}>
                  <ListRow.Content.Title
                    css={[styles.truncate, visibleNamesTitleStyles, isCompactMode && compactModeStyles]}
                    isBold={isBold}
                    isCompactMode={isCompactMode}
                  >
                    {displayTitle}
                  </ListRow.Content.Title>
                </span>
                {shouldShowHiddenNames && (
                  <HiddenNamesOverflow
                    hiddenNames={hiddenNames}
                    hiddenNamesTriggerProps={hiddenNamesTriggerProps}
                    isBold={isBold}
                    isCompactMode={isCompactMode}
                  />
                )}
              </NakedButton>
              <EditTooltip {...editTooltipProps}>{t('Edit Group Chat Name')}</EditTooltip>
            </>
          )}

          {!isEditing && !isEditable && (
            <>
              <ListRow.Content.Title
                css={[styles.truncate, visibleNamesTitleStyles, isCompactMode && compactModeStyles]}
                isBold={isBold}
                isCompactMode={isCompactMode}
              >
                {displayTitle}
              </ListRow.Content.Title>
              {shouldShowHiddenNames && (
                <HiddenNamesOverflow
                  hiddenNames={hiddenNames}
                  hiddenNamesTriggerProps={hiddenNamesTriggerProps}
                  isBold={isBold}
                  isCompactMode={isCompactMode}
                />
              )}
            </>
          )}
        </div>
        {showContactsPaneButton && (
          <NakedButton
            onClick={handleCountClick}
            aria-label={t('Open group participants panel')}
            data-trackingid='group-thread-title-contact-count'
            css={contactsPaneButtonStyles}
          >
            <Icon name='contacts' size={14} />
            <span>{displayNames.length}</span>
          </NakedButton>
        )}
        {trailingIcon}
      </div>
      {shouldShowHiddenNames && (
        <Tooltip {...hiddenNamesTooltipProps}>
          <div css={tooltipContentStyles}>
            {hiddenNames.map((hiddenName, index) => (
              <span key={`${hiddenName}-${index}`}>{hiddenName}</span>
            ))}
          </div>
        </Tooltip>
      )}
    </>
  );
};
